package com.iregained.site.components.sections.error

import androidx.compose.runtime.Composable
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*

// CMS DATA MODEL & MEDIA SPECS
// backgroundIllustration: 16:9 or 1:1 SVG, subtle line-art representing a disconnected node/wire
data class NotFoundSectionData(
    val wittyDiagnosis: String = "Diagnosis: Synaptic Mismatch",
    val title: String = "Apologies, This Pathway is Offline.",
    val description: String = "We cannot locate the requested data stream. It may have been cognitively restructured (moved) or simply lost its signal. Rerouting to the central memory core (Home) is recommended.",
    val primaryCtaText: String = "Return to Home",
    val primaryCtaLink: String = "/",
    val backgroundIllustration: String = Res.Images.NotFound.NOT_FOUND_IMG,
    val secondaryLinks: List<Pair<String, String>> = listOf(
        "About Us" to "/about", "Our Product" to "/product", "Contact Support" to "/contact"
    )
)

val defaultNotFoundData = NotFoundSectionData()

@Composable
fun NotFoundSection(data: NotFoundSectionData = defaultNotFoundData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    // Wrap in a Box to allow layering the background illustration behind the column
    // Wrap in a Box to allow layering the background illustration behind the column
    Box(
        modifier = Modifier.fillMaxWidth().minHeight(70.vh) // Ensure the section takes up a good portion of the screen
            .position(Position.Relative).overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden),
        contentAlignment = Alignment.Center
    ) {
        // Subtle Background Illustration
        Image(
            src = data.backgroundIllustration,
            description = "Disconnected connection background",
            modifier = Modifier.position(Position.Absolute).fillMaxSize()
                .opacity(0.05) // Keep it very subtle so text remains readable
                .zIndex(-1).objectFit(ObjectFit.Cover)
        )

        // Foreground Content
        Column(
            modifier = Modifier.fillMaxWidth()
                // Standard responsive padding per project conventions
                .padding(topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            // Error Code / Diagnosis (e.g., Diagnosis: ...)
            // FIXED STYLING: Significantly reduced the font size to accommodate a phrase
            SpanText(
                text = data.wittyDiagnosis,
                modifier = Modifier.fontSize(if (isDesktop) 54.px else 38.px) // Reduced from 96px/72px
                    .fontWeight(FontWeight.Bold).color(palette.brand.primary)
                    .textAlign(TextAlign.Center) // Added alignment for wrapping text
                    .margin(bottom = 16.px)
            )

            // Title
            SpanText(
                text = data.title,
                modifier = Modifier.fontSize(if (isDesktop) 36.px else 28.px).fontWeight(FontWeight.SemiBold)
                    .margin(bottom = 16.px).textAlign(TextAlign.Center)
            )

            // Description
            SpanText(
                text = data.description,
                modifier = Modifier.maxWidth(600.px).fontSize(18.px).textAlign(TextAlign.Center).margin(bottom = 40.px)
            )

            // Primary CTA Button
            Link(
                path = data.primaryCtaLink,
                modifier = Modifier.backgroundColor(palette.brand.primary).color(Colors.White)
                    .padding(leftRight = 32.px, topBottom = 16.px).borderRadius(8.px).fontWeight(FontWeight.Bold)
                    .textDecorationLine(TextDecorationLine.None).margin(bottom = 48.px)
            ) {
                SpanText(data.primaryCtaText)
            }

            // Helpful Links Section
            SpanText(
                text = "Or try one of these helpful links:",
                modifier = Modifier.fontSize(14.px).margin(bottom = 16.px).opacity(0.7)
            )

            Row(
                modifier = Modifier.gap(24.px).flexWrap(FlexWrap.Wrap), horizontalArrangement = Arrangement.Center
            ) {
                data.secondaryLinks.forEach { (label, path) ->
                    Link(
                        path = path,
                        modifier = Modifier // Note: Replace this with your NotFoundHelpfulLinkStyle.toModifier() if it is defined.
                    ) {
                        SpanText(label)
                    }
                }
            }
        }
    }
}